import { useEffect, useState, FC } from 'react';
import { Card } from '../ui/card';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { MessageCircle, Send, MoreVertical, Flag, AlertTriangle, Pencil, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { CommentItem } from './CommentItem';
import { PostReactions } from './PostReactions';
import { useReports } from '@/hooks/useReports';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from '../ui/textarea';

interface Comment {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  author: {
    student_name: string;
    avatar_url?: string;
  };
  hidden?: boolean;
  hidden_reason?: string;
}

interface CommunityPost {
  id: string;
  user_id: string;
  content: string;
  created_at: string;
  likes_count: number;
  comments_count: number;
  student_name: string;
  avatar_url?: string;
  hidden?: boolean;
  hidden_reason?: string;
}

interface PostProps {
  post: CommunityPost;
  onComment: (postId: string, content: string) => Promise<void>;
  getComments: (postId: string) => Promise<Comment[]>;
  onReport: () => void;
  onEdit: (postId: string, content: string) => Promise<boolean>;
  onDelete: (postId: string) => Promise<boolean>;
}

export const CommunityPost: FC<PostProps> = ({
  post,
  onComment,
  getComments,
  onReport,
  onEdit,
  onDelete,
}) => {
  const [isAuthor, setIsAuthor] = useState(false);
  const [isCommenting, setIsCommenting] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState<Comment[]>([]);
  const [showComments, setShowComments] = useState(false);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [commentCount, setCommentCount] = useState(0);
  const [reportReason, setReportReason] = useState('');
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { createReport } = useReports();
  const { toast } = useToast();

  useEffect(() => {
    const checkAuthor = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setIsAuthor(user?.id === post.user_id);
    };
    checkAuthor();
  }, [post.user_id]);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim()) return;

    setIsCommenting(true);
    try {
      await onComment(post.id, commentText);
      setCommentText('');
      // Recargar comentarios
      const newComments = await getComments(post.id);
      setComments(newComments);
      setCommentCount(newComments.length);
    } finally {
      setIsCommenting(false);
    }
  };

  const handleToggleComments = async () => {
    if (!showComments && comments.length === 0) {
      setIsLoadingComments(true);
      try {
        const fetchedComments = await getComments(post.id);
        setComments(fetchedComments);
        setCommentCount(fetchedComments.length);
      } finally {
        setIsLoadingComments(false);
      }
    }
    setShowComments(!showComments);
  };

  const handleReport = async () => {
    if (!reportReason) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Por favor, selecciona una razón para el reporte.",
      });
      return;
    }

    try {
      const success = await createReport({
        content_type: 'post',
        reported_content: post.content,
        reason: reportReason,
        reported_user_id: post.user_id,
        content_id: post.id,
      });

      if (success) {
        setReportDialogOpen(false);
        setReportReason('');
        toast({
          title: "Reporte enviado",
          description: "Gracias por ayudarnos a mantener la comunidad segura.",
        });
      }
    } catch (error) {
      console.error('Error reporting post:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo enviar el reporte. Por favor, intenta de nuevo.",
      });
    }
  };

  const handleEdit = async () => {
    if (!editContent.trim() || editContent === post.content) {
      setIsEditing(false);
      return;
    }
    try {
      const success = await onEdit(post.id, editContent.trim());
      if (success) {
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Error editing post:', error);
    }
  };

  const handleDelete = async () => {
    try {
      const success = await onDelete(post.id);
      if (success) {
        setDeleteDialogOpen(false);
      }
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  useEffect(() => {
    const fetchCommentCount = async () => {
      const fetchedComments = await getComments(post.id);
      setCommentCount(fetchedComments.length);
    };
    fetchCommentCount();
  }, [post.id]);

  const fetchComments = async () => {
    const fetchedComments = await getComments(post.id);
    setComments(fetchedComments);
    setCommentCount(fetchedComments.length);
  };

  if (post.hidden) {
    return null; // No mostrar posts ocultos
  }

  return (
    <>
      <Card className="p-1 sm:p-2 md:p-4 bg-white/80 backdrop-blur-sm">
        <div className="flex items-start gap-1.5 md:gap-3">
          <Avatar className="w-6 h-6 md:w-9 md:h-9 flex-shrink-0">
            <AvatarImage src={post.author?.avatar_url} alt={post.author?.student_name || 'Usuario sin nombre'} />
            <AvatarFallback className="bg-gradient-to-br from-primary/20 to-primary/30 text-primary-foreground">
              {post.author?.student_name?.[0] || '?'}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1 md:mb-2 flex-wrap gap-1">
              <h3 className="font-semibold text-xs sm:text-sm md:text-base truncate max-w-[180px] sm:max-w-[200px] md:max-w-none">
                {post.author?.student_name || 'Usuario sin nombre'}
              </h3>
              <div className="flex items-center gap-1 sm:gap-2">
                <span className="text-[10px] sm:text-xs text-gray-500 hidden sm:block">
                  {format(new Date(post.created_at), "d 'de' MMMM 'a las' HH:mm", { locale: es })}
                </span>
                <span className="text-[10px] sm:text-xs text-gray-500 sm:hidden">
                  {format(new Date(post.created_at), "d MMM HH:mm", { locale: es })}
                </span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-6 w-6 sm:h-8 sm:w-8 text-gray-500 hover:text-gray-700">
                      <MoreVertical className="h-3 w-3 sm:h-4 sm:w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {isAuthor && (
                      <>
                        <DropdownMenuItem onSelect={() => setIsEditing(true)}>
                          <Pencil className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                          <span>Editar publicación</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => setDeleteDialogOpen(true)}>
                          <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                          <span>Eliminar publicación</span>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuItem onSelect={() => setReportDialogOpen(true)}>
                      <Flag className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
                      <span>Reportar publicación</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {isEditing ? (
              <div className="mt-2">
                <Textarea
                  value={editContent}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="min-h-[100px]"
                />
                <div className="flex justify-end gap-2 mt-2">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setIsEditing(false);
                      setEditContent(post.content);
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button onClick={handleEdit}>
                    Guardar
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm sm:text-base md:text-[1.1rem] leading-relaxed font-medium text-gray-800 whitespace-pre-wrap break-words">
                {post.content}
              </p>
            )}
            <div className="flex flex-col gap-2 mt-2 sm:mt-3">
              <div className="flex items-center">
                <PostReactions postId={post.id} />
              </div>
              
              <div className="flex items-center">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-gray-500 hover:text-gray-700 h-6 sm:h-8"
                  onClick={handleToggleComments}
                >
                  <MessageCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
                  <span className="text-xs sm:text-sm">{commentCount} {commentCount === 1 ? 'comentario' : 'comentarios'}</span>
                </Button>
              </div>
            </div>

            <AnimatePresence>
              {showComments && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="mt-2 sm:mt-3 space-y-1.5 sm:space-y-3">
                    <form onSubmit={handleSubmitComment} className="flex gap-1.5 sm:gap-2">
                      <Input
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder="Escribe un comentario..."
                        className="flex-1 text-xs sm:text-sm h-7 sm:h-8"
                      />
                      <Button 
                        type="submit" 
                        size="icon"
                        disabled={isCommenting || !commentText.trim()}
                        className="h-7 w-7 sm:h-8 sm:w-8"
                      >
                        <Send className="h-3 w-3 sm:h-4 sm:w-4" />
                      </Button>
                    </form>

                    {isLoadingComments ? (
                      <div className="flex justify-center py-1.5 sm:py-2">
                        <div className="animate-spin rounded-full h-4 w-4 sm:h-6 sm:w-6 border-b-2 border-primary"></div>
                      </div>
                    ) : (
                      <div className="space-y-1.5 sm:space-y-2">
                        {comments.map((comment) => (
                          <CommentItem
                            key={comment.id}
                            comment={comment}
                            onCommentUpdate={fetchComments}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </Card>

      {/* Diálogo de Reporte */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reportar publicación</DialogTitle>
            <DialogDescription>
              Por favor, selecciona una razón para reportar esta publicación.
            </DialogDescription>
          </DialogHeader>
          <Select value={reportReason} onValueChange={setReportReason}>
            <SelectTrigger>
              <SelectValue placeholder="Selecciona una razón" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="spam">Spam</SelectItem>
              <SelectItem value="inappropriate">Contenido inapropiado</SelectItem>
              <SelectItem value="harassment">Acoso</SelectItem>
              <SelectItem value="other">Otro</SelectItem>
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReportDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleReport}>
              Reportar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Eliminar publicación?</DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer. ¿Estás seguro de que deseas eliminar esta publicación?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              className="bg-red-500 hover:bg-red-600 text-white"
              onClick={handleDelete}
            >
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
